import { observable, computed, action } from 'mobx';
import CreditCardFormStore, {
  ICreditCardFormStoreAttributes,
} from '../../domain/entities/CreditCardFormStore/CreditCardFormStore';
import {
  codensaCC,
  dinersCC,
  IOtherCCData,
  magnaCC,
  mapStandardToRCCardTypes,
  StandardPaymentCardType,
} from '../../constants/CreditCardTypes';
import validateLuhn from '../../utils/validateLuhn';

const otherCreditCards: IOtherCCData[] = [
  codensaCC, 
  dinersCC,
  magnaCC,
];
 
interface ICreditCardFormViewControllerConfig {
  creditCardFormStore: CreditCardFormStore;
}

interface ICreditCardFormViewControllerData extends ICreditCardFormStoreAttributes {
  isValidForm: boolean;
  isOtherCC: boolean;
  isOtherCCValid: boolean;
  otherCCvalue: string;
  otherCCdata?: IOtherCCData;
}

export default class CreditCardFormViewController {
  @observable creditCardFormStore: CreditCardFormStore;

  @observable private isOtherCCValid = false;

  @observable private isOtherCC = false;

  @observable private otherCCvalue = '';

  @observable private otherCCdata?: IOtherCCData;

  constructor(config: ICreditCardFormViewControllerConfig) {
    this.creditCardFormStore = config.creditCardFormStore;
  }


  loaded = async (): Promise<void> => {
    this.creditCardFormStore.clearStore();
  };

  onCardHolderNameChange = (value: string): void => {
    this.creditCardFormStore.updateCreditCardValues({
      cardHolderName: value,
    });
  };

  @action
  private identifyOtherCC = (): void => {
    this.otherCCdata = otherCreditCards.find(({ pattern }) => pattern.test(this.otherCCvalue));
    this.isOtherCC = Boolean(this.otherCCdata);
  };

  @action
  private validateOtherCC = (): void => {
    this.isOtherCCValid = validateLuhn(this.otherCCvalue) 
      && this.otherCCdata 
      && this.otherCCdata.length === this.otherCCvalue.length;
  };

  private formatCCnumber = (number = ''): string => {
    if (this.isOtherCC && this.otherCCdata) {
      const result = (number.match(this.otherCCdata.format) || [])
        .splice(1, 4)
        .filter(x => x)
        .join(' ');

      return result;
    }
    return number;
  };

  onCardNumberChange = (value: string): void => {
    const cardNumber = (value || '').replace(/\s+/g, '');
    this.creditCardFormStore.updateCreditCardValues({
      cardNumber,
    });
    this.otherCCvalue = cardNumber;
    this.identifyOtherCC();
    this.validateOtherCC();
  };

  onCVVChange = (value: string): void => {
    this.creditCardFormStore.updateCreditCardValues({
      cvv: value,
    });
  };

  onExpiryDateChange = (value: string): void => {
    const splitVal = value.split('/');
    this.creditCardFormStore.updateCreditCardValues({
      expMonth: Number(splitVal[0]),
      expYear: Number(splitVal[1]),
    });
  };

  onToggleSaveCard = (): void => {
    this.creditCardFormStore.updateCreditCardValues({
      shouldSaveCC: !this.creditCardFormStore.values.shouldSaveCC,
    });
  };

  onToggleSetDefaultCard = (): void => {
    this.creditCardFormStore.updateCreditCardValues({
      shouldMakeDefaultCard: !this.creditCardFormStore.values.shouldMakeDefaultCard,
    });
  };

  onCardTypeChange = (cardType: string): void => {
    this.creditCardFormStore.updateCreditCardValues({
      cardType: mapStandardToRCCardTypes[cardType as StandardPaymentCardType] || cardType,
    });
  };

  onInstallmentChange = (value: string): void => {
    this.creditCardFormStore.updateCreditCardValues({
      installments: Number(value),
    });
  };

  onCardHolderNameError = (error: string): boolean => {
    this.creditCardFormStore.setCardHolderNameError(error);

    return this.creditCardFormStore.isValidCCForm;
  };

  onError = (error: string): boolean => {
    this.creditCardFormStore.setGlobalError(error);

    return this.creditCardFormStore.isValidCCForm;
  };

  @computed
  get data(): ICreditCardFormViewControllerData {

    return {
      ...this.creditCardFormStore.values,
      isOtherCC: this.isOtherCC,
      isOtherCCValid: this.isOtherCCValid,
      isValidForm: this.creditCardFormStore.isValidCCForm,
      otherCCdata: this.otherCCdata,
      otherCCvalue: this.isOtherCC && this.formatCCnumber(this.otherCCvalue),
    };
  }
}
